import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { tenantSettingsApi } from '../api/tenantSettingsApi';
import { configurationStatusApi } from '../api/configurationStatusApi';
import type { EnhancedTenantConfig } from '@xtnd/web/shared/types/tenantSettingTypes';
import type { ConfigurationStatusResponse } from '../api/configurationStatusApi';

// Query Keys
export const tenantQueryKeys = {
  all: ['tenant-config'] as const,
  config: ['tenant-config'] as const,
  configurationStatus: ['configuration-status'] as const,
  moduleHealth: (moduleName: string) => ['module-health', moduleName] as const,
};

/**
 * Hook to fetch tenant configuration with connection test
 */
export function useTenantConfig() {
  return useQuery<EnhancedTenantConfig>({
    queryKey: tenantQueryKeys.config,
    queryFn: async () => {
      // Test connection first
      const status = await tenantSettingsApi.testConnection();

      if (!status.connected) {
        throw new Error('API not connected');
      }

      return tenantSettingsApi.getCurrentTenantConfig();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch configuration status
 */
export function useConfigurationStatus(enabled: boolean = true) {
  return useQuery<ConfigurationStatusResponse>({
    queryKey: tenantQueryKeys.configurationStatus,
    queryFn: () => configurationStatusApi.getConfigurationStatus(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    enabled,
  });
}

/**
 * Hook to save tenant settings
 */
export function useTenantSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const currentConfig = await tenantSettingsApi.getCurrentTenantConfig();

      const apiPayload = {
        tenantName: data.tenantName,
        alias: data.alias,
        authorizedDomains: data.authorizedDomains,
        modules: {
          ...currentConfig.modules,
          tenantSettings: {
            contactInfo: {
              admin: data.contactInfo.admin,
              technicalSupport: data.contactInfo.technicalSupport,
              operationalSupport: data.contactInfo.operationalSupport,
            },
            settingsComponent: 'TenantSettingsModal',
            healthCheckEndpoint: '/health/tenantSettings',
          },
          emailServiceConfig: data.defaultFromEmail?.email
            ? {
              ...currentConfig.modules?.emailServiceConfig,
              defaultFromEmail: data.defaultFromEmail.email,
              defaultFromEmailVerified: data.defaultFromEmail.verificationStatus === 'verified',
              verifiedEmails:
                data.defaultFromEmail.verificationStatus === 'verified'
                  ? [
                    ...(currentConfig.modules?.emailServiceConfig?.verifiedEmails || []),
                    data.defaultFromEmail.email,
                  ].filter((email, index, arr) => arr.indexOf(email) === index)
                  : currentConfig.modules?.emailServiceConfig?.verifiedEmails || [],
            }
            : currentConfig.modules?.emailServiceConfig || {},
        },
      };

      return await tenantSettingsApi.updateCurrentTenantConfig(apiPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.config });
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.configurationStatus });
      showToast.success('Tenant settings saved successfully!');
    },
    onError: error => {
      console.error('Tenant settings save error:', error);
      showToast.error('Failed to save tenant settings');
    },
  });
}

/**
 * Hook to check module health
 */
export function useModuleHealthCheck() {
  return useMutation({
    mutationFn: async (moduleName: string) => {
      const health = await tenantSettingsApi.checkModuleHealth(moduleName);
      return { moduleName, health };
    },
    onError: (error, moduleName) => {
      console.error(`Health check failed for ${moduleName}:`, error);
      showToast.error(`Failed to check ${moduleName} health`);
    },
  });
}

